import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Check, Gift, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import api, { fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/api";

const FMT_DATE = fmtDate;

const STATUS_LABEL = {
  trialing: "Trial",
  active: "Active",
  past_due: "Payment overdue",
  canceled: "Canceled",
  incomplete: "Incomplete",
  incomplete_expired: "Expired",
  unpaid: "Unpaid",
  paused: "Paused",
};

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [subscription, setSubscription] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null); // lookup_key currently being requested
  const [portalLoading, setPortalLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setPlans(p.data.plans);
      setTrialDays(p.data.trial_days);
      setSubscription(s.data.subscription);
      setBalance(s.data.free_months_balance);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const status = params.get("status");
    const sessionId = params.get("session_id");
    if (status === "success" && sessionId) {
      toast.success("Subscription activated — welcome aboard.");
      // Poll status until finalized, then reload
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/billing/status/${sessionId}`);
          if (data.status === "complete") {
            load();
            setParams({}, { replace: true });
            return;
          }
        } catch (e) { /* ignore transient */ }
        if (tries++ < 8) setTimeout(poll, 1200);
        else { load(); setParams({}, { replace: true }); }
      };
      poll();
    } else if (status === "cancel") {
      toast.info("Checkout canceled — no charge made.");
      setParams({}, { replace: true });
    }
  }, [params, setParams, load]);

  const subscribe = async (lookupKey) => {
    setCheckingOut(lookupKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setCheckingOut(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-ink-lighter">Loading…</div>;

  const isSubscribed = subscription && ["trialing", "active", "past_due"].includes(subscription.status);
  const currentPlanKey = subscription?.plan_key;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="billing-page">
      <div className="mb-8">
        <div className="overline mb-2">Billing</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">
          {isSubscribed ? "Manage your subscription" : "Choose your plan"}
        </h1>
        <p className="text-ink-light mt-2">
          {isSubscribed
            ? "Your subscription, invoices, and payment method — all in one place."
            : "Every plan includes a 14-day free trial. Cancel anytime."}
        </p>
      </div>

      {/* Free-month balance callout */}
      {balance > 0 && !isSubscribed && (
        <div
          className="card-surface p-5 mb-8 border-l-4 border-l-ochre bg-ochre/5 flex items-start gap-3"
          data-testid="billing-free-months-banner"
        >
          <div className="w-9 h-9 rounded-lg bg-ochre text-white flex items-center justify-center shrink-0">
            <Gift className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-medium text-ink">
              You have <span className="num font-heading text-ochre" data-testid="billing-balance">{balance}</span> free month{balance > 1 ? "s" : ""} from referrals
            </div>
            <p className="text-sm text-ink-light mt-1">
              These will be auto-applied at checkout — you won&apos;t be charged until they run out.
            </p>
          </div>
        </div>
      )}

      {/* Current subscription card */}
      {isSubscribed && (
        <div className="card-surface p-6 mb-8" data-testid="billing-current-subscription">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="overline mb-1">Current plan</div>
              <div className="flex items-center gap-3 mb-1">
                <div className="font-heading text-2xl font-medium capitalize" data-testid="billing-current-plan">
                  {currentPlanKey || "Subscribed"}
                </div>
                <span
                  className={`text-xs font-medium px-2 py-1 rounded-md ${
                    subscription.status === "active" ? "bg-forest/10 text-forest"
                    : subscription.status === "trialing" ? "bg-ochre/10 text-ochre"
                    : "bg-destructive/10 text-destructive"
                  }`}
                  data-testid="billing-status-badge"
                >
                  {STATUS_LABEL[subscription.status] || subscription.status}
                </span>
              </div>
              <div className="text-sm text-ink-light">
                {subscription.status === "trialing" && subscription.trial_end
                  ? <>Trial ends {FMT_DATE(subscription.trial_end)}</>
                  : <>Next invoice {FMT_DATE(subscription.current_period_end)}</>}
                {subscription.cancel_at_period_end && (
                  <span className="ml-2 text-destructive">· Cancels at period end</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={openPortal}
              disabled={portalLoading}
              className="btn-forest inline-flex items-center gap-2"
              data-testid="billing-manage-btn"
            >
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Manage billing
            </button>
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div className="grid md:grid-cols-2 gap-6" data-testid="billing-plans">
        {plans.map((plan) => {
          const isCurrent = currentPlanKey === plan.plan_key;
          const lookup = `${plan.plan_key}_monthly`;
          return (
            <div
              key={plan.plan_key}
              className={`card-surface p-6 flex flex-col ${
                plan.plan_key === "studio" ? "border-forest border-2" : ""
              }`}
              data-testid={`plan-${plan.plan_key}`}
            >
              {plan.plan_key === "studio" && (
                <div className="overline text-forest mb-2">Most popular</div>
              )}
              <div className="font-heading text-2xl font-medium">{plan.name}</div>
              <div className="flex items-baseline gap-1 mt-2 mb-4">
                <span className="num font-heading text-4xl font-medium">
                  {fmtMoney(plan.amount / 100)}
                </span>
                <span className="text-ink-lighter text-sm">/ month</span>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-ink-light">
                    <Check className="w-4 h-4 text-forest mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <button
                  type="button"
                  disabled
                  className="btn-secondary w-full opacity-70 cursor-default"
                  data-testid={`plan-${plan.plan_key}-current`}
                >
                  Current plan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => subscribe(lookup)}
                  disabled={checkingOut === lookup}
                  className={`w-full inline-flex items-center justify-center gap-2 ${
                    plan.plan_key === "studio" ? "btn-forest" : "btn-secondary"
                  }`}
                  data-testid={`plan-${plan.plan_key}-subscribe`}
                >
                  {checkingOut === lookup ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" strokeWidth={1.75} />
                      {isSubscribed ? `Switch to ${plan.name}` : `Start ${trialDays}-day trial`}
                    </>
                  )}
                </button>
              )}
              {!isCurrent && balance > 0 && (
                <p className="text-[11px] text-ochre text-center mt-2" data-testid={`plan-${plan.plan_key}-discount-note`}>
                  {balance} free month{balance > 1 ? "s" : ""} will apply first — no card charge until then.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center gap-2 text-xs text-ink-lighter">
        <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
        Secure checkout by Stripe. Cancel anytime — no long-term contracts.
      </div>
    </div>
  );
}
